import {
  Opportunity,
  OpportunityDataTableResponse,
  OpportunityFieldGenerationResponse,
  OpportunityShareTokenGenerationResponse, OpportunityTableFilters,
  IssueFeedback, IssueFeedbackComment
} from "@/opportunity/opportunity";

interface ChangeStatusResponse {
  message: string;
  status_change_id: number;
}
import { apiClient } from "@/api/client";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { get, post } from "@/api/api";
import { useState } from "react";
import { formatISO } from "date-fns";
import { useParams } from "@tanstack/react-router";
import { SortingState } from "@tanstack/react-table";
import { EmailRecipient, UserMinimal } from "@/user/user";

export const updateOpportunity = async (
  opportunityId: number,
  stateUpdate: Partial<Opportunity>
) => {
  const res = await apiClient.patch<Opportunity>(`/opportunities/${opportunityId}/update`, stateUpdate);
  return res.data;
};

export const opportunities = createQueryKeys('opportunities', {
  list: (
    {
      page = 1,
      pageSize = 10,
      sorting = [],
      filters = {},
    }: {
      page?: number;
      pageSize?: number;
      sorting?: SortingState;
      filters?: OpportunityTableFilters;
    }
  ) => ({
    queryKey: [page, pageSize, sorting, filters],
    queryFn: async () => {
      let url = `/opportunities/?page=${page}&page_size=${pageSize}`;
      if (sorting.length) {
        url += `&ordering=${sorting[0]?.desc ? "-" : ""}${sorting[0]?.id}`;
      }
      if (filters.location) {
        url += `&location=${filters.location}`;
      }
      if (filters.category) {
        url += `&category=${filters.category}`;
      }
      if (filters.organization_id !== null && filters.organization_id !== undefined) {
        url += `&organization_id=${filters.organization_id}`;
      }
      if (filters.installation_ids && filters.installation_ids.length) {
        url += `&installation_ids=${filters.installation_ids}`;
      }
      if (filters.status && filters.status.length) {
        url += `&status=${filters.status.join(",")}`;
      }
      if (filters.requires_action !== undefined) {
        url += `&requires_action=${filters.requires_action}`
      }
      return await get<OpportunityDataTableResponse>(url);
    }
  }),
  detail: (
    opportunityId: string | number | undefined,
    sharedToken?: string
  ) => ({
    queryKey: [String(opportunityId), sharedToken],
    queryFn: async () => {
      if (sharedToken) {
        return await get<Opportunity>(`/opportunities/shared/${sharedToken}/`);
      } else {
        return await get<Opportunity>(`opportunities/${opportunityId}/`);
      }
    },
  }),
  nonStaffUsers: (opportunityId: string | number) => ({
    queryKey: [opportunityId],
    queryFn: async () =>
      await get<UserMinimal[]>(`/opportunities/${opportunityId}/non-staff-users/`)
  }),
  emailRecipients: (opportunityId: string | number) => ({
    queryKey: ['emailRecipients', opportunityId],
    queryFn: async () =>
      await get<EmailRecipient[]>(`/opportunities/${opportunityId}/email-recipients/`)
  }),
});

const useOpportunityDetailQueryDefinition = (id?: number | string, sharedToken?: string) => {
  return opportunities.detail(id ? String(id) : undefined, sharedToken);
};

export const useOpportunityData = () => {
  const { token, opportunityId } = useParams({ strict: false });
  if (!opportunityId && !token) {
    throw Error("No opportunity ID or sharedToken provided")
  }
  const query = useOpportunityDetailQueryDefinition(opportunityId, token)
  return useSuspenseQuery(query);
};

export const useOpportunities = (
  {
    page = 1,
    pageSize = 10,
    sorting = [],
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    sorting?: SortingState;
    filters?: OpportunityTableFilters,
  }
) => useSuspenseQuery(opportunities.list({ page, pageSize, sorting, filters }));

export const useOpportunityMutation = (
  opportunity: Opportunity,
  onSuccess?: () => void,
  onError?: () => void,
) => {
  const [hasErrored, setHasErrored] = useState(false);

  const queryClient = useQueryClient()
  const query = useOpportunityDetailQueryDefinition(opportunity.id)

  const mutation = useMutation({
    mutationFn: (update: Partial<Opportunity>) => updateOpportunity(opportunity.id, update),
    onSuccess: (data: Opportunity) => {
      setHasErrored(false);
      queryClient.setQueryData(query.queryKey, data)
      onSuccess && onSuccess();
    },
    onError: () => {
      setHasErrored(true);
      onError && onError();
    }
  });
  return { mutation, hasErrored };
}

export const useOpportunityChangeStatusMutation = (opportunity: Opportunity) => {
  const queryClient = useQueryClient();
  const opportunityDetailQueryDefinition = useOpportunityDetailQueryDefinition(opportunity.id);

  return useMutation({
    mutationFn: async ({ opportunityId, targetStatus, occurredOn, comments, reason }: {
      opportunityId: number,
      targetStatus: string,
      occurredOn: Date,
      comments?: string,
      reason?: string,
    }): Promise<ChangeStatusResponse> => {
      const response = await post(`opportunities/${opportunityId}/change-status/`, {
        comments,
        reason,
        target_status: targetStatus,
        occurred_on: formatISO(occurredOn, { representation: "date" })
      });
      return response as ChangeStatusResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: opportunityDetailQueryDefinition.queryKey,
      });
      return queryClient.invalidateQueries({
        queryKey: opportunities.list._def
      });
    }
  })
}

export const useOpportunityDeleteStatusChangeMutation = (opportunityId: number) => {
  const queryClient = useQueryClient();
  const opportunityDetailQueryDefinition = useOpportunityDetailQueryDefinition(opportunityId);

  return useMutation({
    mutationFn: async (statusChangeId: number) => {
      await apiClient.delete<Opportunity>(`opportunities/delete-opportunity-status-change/${statusChangeId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: opportunityDetailQueryDefinition.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: opportunities.list._def
      });
    }
  })
}

export const useOpportunityGenerateIssueSummaryMutation = (opportunityId: number) => {
  const queryClient = useQueryClient();
  const opportunityDetailQueryDefinition = useOpportunityDetailQueryDefinition(opportunityId);

  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<OpportunityFieldGenerationResponse>(`opportunities/${opportunityId}/generate-issue-summary/`);
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: opportunityDetailQueryDefinition.queryKey,
      });
    }
  })
}

export const useGenerateShareableLink = (opportunityId: number, onSuccess?: () => void, onError?: (error: Error) => void) => {
  const mutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<OpportunityShareTokenGenerationResponse>(`opportunities/${opportunityId}/generate-token/`);
      const token = response.data.token;
      return `${window.location.origin}/issues/shared/${token}`;
    },
    onSuccess: (link) => {
      onSuccess && onSuccess();
      return link;
    },
    onError: (error) => {
      onError && onError(error);
      console.error("Error generating share link:", error);
    }
  });

  const copyToClipboard = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      return true;
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      return false;
    }
  };

  const generateShareableLink = async () => {
    try {
      return await mutation.mutateAsync();
    } catch (error) {
      throw error;
    }
  };

  const generateAndCopyShareableLink = async () => {
    try {
      const link = await mutation.mutateAsync();
      await copyToClipboard(link);
      return link;
    } catch (error) {
      throw error;
    }
  };

  return {
    generateShareableLink,
    generateAndCopyShareableLink,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
}

export const useNonStaffUsers = (opportunityId: number) => {
  return useSuspenseQuery(opportunities.nonStaffUsers(opportunityId))
}

export const useEmailRecipients = (opportunityId: number, enabled = true) => {
  const queryDef = opportunities.emailRecipients(opportunityId);
  return useQuery({ ...queryDef, enabled });
}

export const useSendNotification = (opportunityId: number) => {
  return useMutation({
    mutationFn: async (recipientIds: number[]) => {
      const res = await apiClient.post<{ sent: number; failed: number }>(
        `opportunities/${opportunityId}/send-notification/`,
        { recipient_ids: recipientIds }
      );
      return res.data;
    },
  });
}

export const useSubmitFeedback = (opportunityId: number) => {
  const queryClient = useQueryClient();
  const opportunityDetailQueryDefinition = useOpportunityDetailQueryDefinition(opportunityId);

  return useMutation({
    mutationFn: async (payload: {
      useful_meter: "thumbs_up" | "thumbs_down";
      comment?: IssueFeedbackComment;
      text?: string;
    }): Promise<IssueFeedback> => {
      const res = await apiClient.post<IssueFeedback>(
        `opportunities/${opportunityId}/feedback/`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: opportunityDetailQueryDefinition.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: opportunities.list._def,
      });
    },
  });
};

export const useUpdateFeedback = (opportunityId: number) => {
  const queryClient = useQueryClient();
  const opportunityDetailQueryDefinition = useOpportunityDetailQueryDefinition(opportunityId);

  return useMutation({
    mutationFn: async ({
      feedbackId,
      payload,
    }: {
      feedbackId: number;
      payload: {
        useful_meter?: "thumbs_up" | "thumbs_down";
        comment?: IssueFeedbackComment | null;
      };
    }): Promise<IssueFeedback> => {
      const res = await apiClient.patch<IssueFeedback>(
        `opportunities/${opportunityId}/feedback/${feedbackId}/`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: opportunityDetailQueryDefinition.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: opportunities.list._def,
      });
    },
  });
};